<script setup lang="ts">
import { onMounted, ref } from "vue";

import { ApiError, api, type CurrentUser, type SiteSummary } from "./api";
import BusinessProfileEditor from "./BusinessProfileEditor.vue";
import SiteEditor from "./SiteEditor.vue";

const user = ref<CurrentUser | null>(null);
const sites = ref<SiteSummary[]>([]);
const selectedSite = ref<SiteSummary | null>(null);
const email = ref("");
const password = ref("");
const loading = ref(true);
const submitting = ref(false);
const error = ref("");

async function loadSites() {
  sites.value = (await api.sites()).sites;
}

async function initialise() {
  try {
    user.value = await api.me();
    await loadSites();
  } catch (caught) {
    if (!(caught instanceof ApiError && caught.status === 401)) {
      error.value = caught instanceof Error ? caught.message : "Unable to load the CMS";
    }
  } finally {
    loading.value = false;
  }
}

async function signIn() {
  error.value = "";
  submitting.value = true;
  try {
    user.value = await api.login(email.value, password.value);
    password.value = "";
    await loadSites();
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to sign in";
  } finally {
    submitting.value = false;
  }
}

async function signOut() {
    await api.logout();
    user.value = null;
    sites.value = [];
    selectedSite.value = null;
}

onMounted(initialise);
</script>

<template>
  <main v-if="loading" class="centered">
    <p>Loading Patchwork…</p>
  </main>

  <main v-else-if="!user" class="centered">
    <section class="login-panel">
      <div class="brand-mark">P</div>
      <p class="eyebrow">Patchwork CMS</p>
      <h1>Welcome back</h1>
      <p class="muted">Sign in to manage your business website.</p>
      <form @submit.prevent="signIn">
        <label>
          Email address
          <input v-model.trim="email" type="email" autocomplete="email" required>
        </label>
        <label>
          Password
          <input v-model="password" type="password" autocomplete="current-password" required>
        </label>
        <p v-if="error" class="error" role="alert">{{ error }}</p>
        <button type="submit" :disabled="submitting">
          {{ submitting ? "Signing in…" : "Sign in" }}
        </button>
      </form>
    </section>
  </main>

  <SiteEditor
    v-else-if="selectedSite && user.isPlatformStaff"
    :site="selectedSite"
    @back="selectedSite = null"
  />

  <BusinessProfileEditor
    v-else-if="selectedSite"
    :site="selectedSite"
    @back="selectedSite = null"
  />

  <div v-else class="app-shell">
    <aside>
      <div>
        <div class="brand"><span class="brand-mark small">P</span> Patchwork</div>
        <nav aria-label="Primary navigation">
          <a href="#" aria-current="page">Websites</a>
          <a href="#">Media</a>
          <a href="#">Account</a>
        </nav>
      </div>
      <button class="quiet" type="button" @click="signOut">Sign out</button>
    </aside>

    <main class="dashboard">
      <header>
        <div>
          <p class="eyebrow">Dashboard</p>
          <h1>Your websites</h1>
        </div>
        <span>{{ user.displayName || user.email }}</span>
      </header>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <section v-if="sites.length" class="site-grid">
        <article v-for="site in sites" :key="site.id" class="site-card">
          <div class="site-preview">{{ site.name.slice(0, 1) }}</div>
          <div class="site-content">
            <p class="eyebrow">{{ site.tenantName }}</p>
            <h2>{{ site.name }}</h2>
            <p class="muted">{{ site.domains.join(", ") || "No domain configured" }}</p>
            <div class="site-classification">
              <span>{{ site.businessType?.name || "No business type" }}</span>
              <span v-if="site.availablePlugins.length">
                Plugins: {{ site.availablePlugins.map((plugin) => plugin.name).join(", ") }}
              </span>
            </div>
            <div class="card-actions">
              <button type="button" @click="selectedSite = site">Manage website</button>
              <span class="role">{{ site.role }}</span>
            </div>
          </div>
        </article>
      </section>
      <section v-else class="empty-state">
        <h2>No websites yet</h2>
        <p>Your account is active, but it has not been added to a website.</p>
      </section>
    </main>
  </div>
</template>
